import { ElMessage } from "element-plus";
import { computed, MaybeRef, ref, unref } from "vue";
import {
  FormElementBase,
  FormElementType,
  FormResponse,
  FormResponseAnswer,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { clearErrors, showError } from "~/utils/handleAPIError";
import { getAnswerHasAnyValue } from "./rulesEngine";
import {
  AnswerValue,
  FormAnswerPlus,
  FormResponseAnswerMap,
  UpdateAnswerApiFunc,
  UpdateAnswerApiResult,
} from "./types";

/**
 * Debounce time in milliseconds for saving answers to prevent excessive API calls
 * This delay helps reduce server load when users are typing quickly
 */
const SET_ANSWER_DEBOUNCE = 500;

/**
 * Global map of timeout IDs for debounced answer submissions
 *
 * This map tracks all pending answer submission requests to:
 * 1. Allow cancellation of pending submissions when a new value is entered
 * 2. Prevent duplicate submissions for the same question
 * 3. Track in-flight requests across the application
 */
export const setAnswerTimeoutMap = ref(
  new Map<FormElementBase["questionCode"], NodeJS.Timeout>(),
);

/**
 * A debounced answer save that hasn't finished persisting yet.
 * `flush` runs the (still-debounced) save immediately; `done` settles once the
 * `submitAnswer`/`deleteAnswer` round-trip completes. Used to await persistence
 * before follow-up actions that re-read the saved answer server-side.
 */
interface PendingSave {
  flush: () => void;
  done: Promise<void>;
}

/**
 * Pending saves keyed by question code. Unlike {@link setAnswerTimeoutMap}
 * (which only tracks the debounce timer and is cleared the moment the timer
 * fires), an entry here lives until the network save actually settles.
 */
const pendingSaveMap = ref(
  new Map<FormElementBase["questionCode"], PendingSave>(),
);

/**
 * Flushes pending debounced answer saves and resolves once they have persisted.
 *
 * Pass a `questionCode` to flush a single answer, or omit it to flush every
 * pending save. Use this before triggering server-side actions that re-read the
 * saved answers (e.g. people migration) so they don't race the debounced save
 * and read a stale value.
 */
export async function flushPendingAnswerSaves(
  questionCode?: FormElementBase["questionCode"],
): Promise<void> {
  const pending =
    questionCode !== undefined
      ? [pendingSaveMap.value.get(questionCode)].filter(
          (p): p is PendingSave => !!p,
        )
      : Array.from(pendingSaveMap.value.values());

  // Kick every pending save off the debounce immediately, then wait for the
  // network round-trips to settle.
  pending.forEach((p) => p.flush());
  await Promise.all(pending.map((p) => p.done));
}

/**
 * Configuration interface for the useAnswerWrapper composable
 */
interface UseAnswerArgs {
  /** The reactive map of all form answers */
  answers: MaybeRef<FormResponseAnswerMap>;

  /** The current form response being edited (optional if not submitting to API) */
  formResponse?: FormResponse;

  /**
   * Function to reload answers after API operations
   * @param answerList - Updated list of answers from the server
   * @param deletingGuid - Optional GUID of an answer that was deleted
   * @param deletingRepeaterGuid - Optional repeater GUID for a deleted answer
   * @param originToken - Token of the local edit this reload is the response to,
   *   used to drop stale in-flight responses that would clobber a newer edit.
   */
  reloadAnswers: (
    answerList: FormResponseAnswer[],
    deletingGuid?: string,
    deletingRepeaterGuid?: string,
    originToken?: number,
  ) => void;

  /** Function to refresh recently updated answers from the server */
  refreshRecentlyUpdatedAnswers: () => void;

  /** Record a new local (optimistic) edit for an answer key; returns its token. */
  noteLocalEdit: (key: string) => number;

  /** Mark a save (by its token) as settled once its API call resolves. */
  settleLocalEdit: (key: string, token: number) => void;

  /** Whether the given token is still the newest local edit for the key. */
  isLatestLocalEdit: (key: string, token: number) => boolean;
}

/**
 * Creates a composable for managing form element answers with API integration
 *
 * This wrapper function creates a specialized useAnswer composable that:
 * 1. Provides a reactive reference to an answer value
 * 2. Handles local state updates
 * 3. Submits answer changes to the API with debouncing
 * 4. Supports custom API update functions for special cases
 *
 * @param {UseAnswerArgs} config - Configuration for the answer management
 * @returns A function that creates a reactive answer reference for a form element
 */
export function useAnswerWrapper({
  answers,
  formResponse,
  reloadAnswers,
  refreshRecentlyUpdatedAnswers,
  noteLocalEdit,
  settleLocalEdit,
  isLatestLocalEdit,
}: UseAnswerArgs) {
  /**
   * Creates a reactive reference to an answer for a specific form element
   *
   * @template TAnswerType - The form element type, which determines the answer value type
   * @param {FormElementBase} el - The form element to create an answer for
   * @param {UpdateAnswerApiFunc<TAnswerType>} updateFunc - Optional custom API update function
   * @returns A computed ref that gets/sets the answer value with API integration
   */
  return function <TAnswerType extends FormElementType>(
    el: FormElementBase,
    updateFunc?: UpdateAnswerApiFunc<TAnswerType>,
  ) {
    /**
     * Updates an answer value locally and submits it to the API with debouncing
     *
     * This function:
     * 1. Updates the local answer state immediately
     * 2. Debounces API submissions to prevent excessive requests
     * 3. Handles both creating new answers and updating existing ones
     * 4. Provides user feedback on save/delete operations
     *
     * @param {AnswerValue<TAnswerType>} newVal - The new answer value to set
     * @param {FormElementBase} element - The form element being answered
     * @param {UpdateAnswerApiFunc<TAnswerType>} apiUpdateFunc - Optional custom API update function
     * @returns {AnswerValue<TAnswerType>} The new answer value (for chaining)
     */
    function setAnswer(
      newVal: AnswerValue<TAnswerType>,
      element: FormElementBase,
      apiUpdateFunc?: UpdateAnswerApiFunc<TAnswerType>,
    ): AnswerValue<TAnswerType> {
      // Build the composite key the same way the getter (and the answers map)
      // does: "questionCode-repeatableGroupGuid". Using the bare questionCode
      // here writes the optimistic update to a key nothing reads, so the UI
      // only reflected the change after the debounced save round-tripped.
      const repeatableGroupGuid = element.repeaterGuid ?? emptyGuid;
      const answerKey = element.questionCode + "-" + repeatableGroupGuid;

      // Get the existing answer for this element, if any
      const answerToSet: FormAnswerPlus<TAnswerType> = unref(answers)[
        answerKey
      ] as FormAnswerPlus<TAnswerType>;

      // Snapshot the prior state so a failed save can roll back the optimistic
      // change instead of leaving the UI out of sync with the server (e.g. a
      // "deleted" answer that the server actually rejected).
      const hadPriorEntry = !!answerToSet;
      const priorValue = answerToSet?.answerValue;

      // Update the local answer state immediately
      if (!answerToSet) {
        // Create a new answer if one doesn't exist
        unref(answers)[answerKey] = {
          formElementGuid: element.guid,
          answerValue: newVal,
        };
      } else {
        // Update the existing answer
        unref(answers)[answerKey].answerValue = newVal;
      }

      // If no form response is provided, we're in local-only mode (no API submission)
      if (!formResponse) {
        return newVal;
      }

      // Stamp this edit so stale in-flight responses can be dropped. Only the
      // default save path is guarded (custom apiUpdateFuncs manage their own
      // reloads); for those we leave the token undefined and behave as before.
      // The token is taken at edit time (not at debounce-fire time) so a refresh
      // arriving during the debounce window also can't clobber the new value.
      const editToken = apiUpdateFunc ? undefined : noteLocalEdit(answerKey);

      // Revert the optimistic local change when a save/delete fails, so the UI
      // reflects what the server actually has. Only the guarded default path can
      // roll back, and only while this edit is still the newest one — a later
      // edit must not be clobbered by an older save's failure.
      const rollbackOptimisticChange = () => {
        if (editToken === undefined) return;
        if (!isLatestLocalEdit(answerKey, editToken)) return;

        if (hadPriorEntry) {
          const entry = unref(answers)[answerKey];
          if (entry) {
            entry.answerValue = priorValue;
          } else {
            unref(answers)[answerKey] = {
              formElementGuid: element.guid,
              answerValue: priorValue,
            };
          }
        } else {
          // The optimistic change created this entry; remove it entirely.
          delete unref(answers)[answerKey];
        }
      };

      // Cancel any pending API submission for this question. The superseded
      // pending-save entry is replaced below; its orphaned `done` simply never
      // settles (nothing awaits it once replaced).
      clearTimeout(setAnswerTimeoutMap.value.get(element.questionCode));

      // Resolver for this save's `done` promise (settles when the round-trip
      // finishes, whether the debounce fired or it was flushed early).
      let markDone!: () => void;
      const done = new Promise<void>((resolve) => {
        markDone = resolve;
      });

      // Runs the actual submission. Idempotent: the debounce timer and an
      // explicit flush both call it, but it only fires once.
      let hasSubmitted = false;
      const submitNow = () => {
        if (hasSubmitted) return;
        hasSubmitted = true;

        clearTimeout(setAnswerTimeoutMap.value.get(element.questionCode));
        setAnswerTimeoutMap.value.delete(element.questionCode);

        // Double-check form response exists (defensive programming)
        if (!formResponse) {
          if (pendingSaveMap.value.get(element.questionCode)?.done === done) {
            pendingSaveMap.value.delete(element.questionCode);
          }
          markDone();
          return;
        }

        // Submit the answer to the API. The default path receives the edit token
        // so its reload can be ignored if superseded by a newer local edit.
        const updatePromise = apiUpdateFunc
          ? apiUpdateFunc(newVal, element, formResponse)
          : defaultApiUpdateFunc(newVal, element, formResponse, editToken);

        updatePromise
          .then((res) => {
            // Provide user feedback based on the operation result
            switch (res.status) {
              case "updated":
                // Answer was successfully saved
                ElMessage.success({
                  message: "Saved",
                  grouping: true,
                });
                if (import.meta.env.DEV) {
                  console.log("Saved", element.questionCode);
                }
                break;
              case "deleted":
                // Answer was successfully deleted
                ElMessage.warning({
                  message: "Removed",
                  grouping: true,
                });
                if (import.meta.env.DEV) {
                  console.warn("Removed", element.questionCode);
                }
                break;
              case "error":
                // The server rejected the change (defaultApiUpdateFunc already
                // surfaced the error). Undo the optimistic edit so the field
                // doesn't show a phantom save/delete.
                rollbackOptimisticChange();
                break;
            }

            // For custom update functions, refresh all recently updated answers
            // This ensures computed questions and other dependent values are updated
            if (apiUpdateFunc) {
              refreshRecentlyUpdatedAnswers();
            }
          })
          .catch((error) => {
            // A thrown error (e.g. from a custom apiUpdateFunc) also leaves an
            // unconfirmed optimistic value; roll it back before reporting.
            rollbackOptimisticChange();
            showError(error);
          })
          .finally(() => {
            // Mark this edit's save as settled so later refreshes for this key
            // are allowed through again once nothing newer is pending.
            if (editToken !== undefined) {
              settleLocalEdit(answerKey, editToken);
            }
            // Drop the tracking entry (unless a newer edit already replaced it)
            // and release anyone awaiting this save via flushPendingAnswerSaves.
            if (pendingSaveMap.value.get(element.questionCode)?.done === done) {
              pendingSaveMap.value.delete(element.questionCode);
            }
            markDone();
          });
      };

      // Create a new debounced API submission
      const timeoutId = setTimeout(submitNow, SET_ANSWER_DEBOUNCE);

      // Store the timeout ID in the map for potential cancellation, and the
      // pending save so callers can flush + await persistence.
      setAnswerTimeoutMap.value.set(element.questionCode, timeoutId);
      pendingSaveMap.value.set(element.questionCode, {
        flush: submitNow,
        done,
      });

      // Return the new value immediately (don't wait for API)
      return newVal;
    }

    /**
     * Default implementation for submitting answers to the API
     *
     * This function:
     * 1. Determines whether to create/update or delete an answer based on its value
     * 2. Makes the appropriate API call
     * 3. Updates the local answer state with the server response
     * 4. Handles errors and provides appropriate return values
     *
     * @param {AnswerValue<TAnswerType> | null} newVal - The new answer value to submit
     * @param {FormElementBase} element - The form element being answered
     * @param {FormResponse} response - The form response being edited
     * @returns {Promise<UpdateAnswerApiResult<TAnswerType>>} Result of the API operation
     */
    async function defaultApiUpdateFunc(
      newVal: AnswerValue<TAnswerType> | null,
      element: FormElementBase,
      response: FormResponse,
      originToken?: number,
    ): Promise<UpdateAnswerApiResult<TAnswerType>> {
      // Clear any previous API errors
      clearErrors();

      // If the answer has a value, create or update it
      if (getAnswerHasAnyValue(newVal, element.type)) {
        try {
          // Submit the answer to the API
          const updatedAnswerList =
            await apiClient.formResponseAnswer.submitAnswer(
              response.guid,
              element.guid,
              element.repeaterGuid ?? emptyGuid,
              element.questionCode ?? "",
              response.formDefinitionGuid,
              newVal,
            );

          // Update all answers with the server response
          // This ensures computed questions and dependent values are updated.
          // originToken lets reloadAnswers drop this write if a newer local edit
          // has happened since (stale in-flight response).
          reloadAnswers(updatedAnswerList, undefined, undefined, originToken);

          // Return success status with the submitted value
          return {
            status: "updated",
            value: newVal,
          };
        } catch (error) {
          // Handle and display any API errors
          showError(error);
          return {
            status: "error",
            value: null,
          };
        }
      } else {
        // If the answer has no value, delete it
        try {
          // Delete the answer via the API
          const updatedAnswerList =
            await apiClient.formResponseAnswer.deleteAnswer(
              response.formDefinitionGuid,
              response.guid,
              element.guid,
              element.repeaterGuid ?? emptyGuid,
            );

          // Update the answers map, specifying which answer was deleted
          // This ensures it's properly removed from the local state
          reloadAnswers(
            updatedAnswerList,
            element.guid,
            element.repeaterGuid ?? emptyGuid,
            originToken,
          );

          // Return deleted status
          return {
            status: "deleted",
            value: null,
          };
        } catch (error) {
          // Handle and display any API errors
          showError(error);
          return {
            status: "error",
            value: null,
          };
        }
      }
    }

    /**
     * Create a computed property that provides a reactive interface to the answer
     *
     * The computed property:
     * - Getter: Retrieves the current answer value from the answers map
     * - Setter: Updates the answer value and submits it to the API
     *
     * This allows for simple v-model binding in templates
     */
    const res = computed<any>({
      get: () => {
        // Create the composite key using question code and repeater GUID
        const repeatableGroupGuid = el.repeaterGuid ?? emptyGuid;
        const key = el.questionCode + "-" + repeatableGroupGuid;

        // Return the current answer value from the answers map
        return unref(answers)[key]?.answerValue;
      },
      set: (newVal: AnswerValue<TAnswerType>) =>
        // When the value is set, update it locally and submit to API
        setAnswer(newVal, el, updateFunc),
    });

    return res;
  };
}
